import { computed, nextTick, ref, watch, type Ref } from 'vue';
import { findTypeaheadTarget } from './typeahead';
import {
  siblingAt,
  buildRootRow,
  ancestorOpenPath,
  type RootRowEntry,
  type OverflowMarker,
} from './focusNav';
import { focusPanel, opensPanel, panelIdOf } from './panel';
import type { NavBarNode } from './types';

export const OVERFLOW_MARKER: OverflowMarker = Object.freeze({
  id: '__overflow__',
  isOverflow: true,
});

export function useNavBarKeyboard(
  leftItems: Ref<NavBarNode[]>,
  rightItems: Ref<NavBarNode[]>,
  maxVisibleLeft: Ref<number>,
  /**
   * Synthetic node representing the "..." overflow root, populated with
   * overflowed left items as its children. NavBar keeps it in sync.
   */
  overflowNode: Ref<NavBarNode | null>
) {
  const focusedNode = ref<NavBarNode | null>(null);
  /** Open chain from root to deepest. */
  const openPath = ref<NavBarNode[]>([]);
  /**
   * True while the user is "in menu mode" — a menu was opened and the user
   * has not yet exited via Escape, outside-click, or a leaf action. Distinct
   * from `openPath.length > 0` because hovering a no-submenu root closes the
   * popover but should keep menu mode alive so further hovers re-open menus.
   */
  const menuActive = ref(false);

  /**
   * A root entry's panel is open. It shares `openPath` with the menus, so
   * only one popover of the bar is ever open, but it is no menu: opening it
   * leaves menu mode, hence hovers open nothing.
   */
  const panelOpen = computed(() => openPath.value.length === 1 && opensPanel(openPath.value[0]));
  // An open entry that stops opening its panel (its `panel` removed, moved
  // into „…“, given children) or gets disabled must not leave its open path
  // behind: Escape and the arrows would misread it, a disabled entry could not
  // be clicked shut, and the panel would reopen by itself once the entry
  // qualifies again. A menu taking over sets menu mode; a regular close has
  // already emptied the path.
  const panelUsable = computed(() => panelOpen.value && !openPath.value[0].disabled);
  watch(panelUsable, (usable, wasUsable) => {
    if (!wasUsable || usable || menuActive.value || openPath.value.length === 0) return;
    const node = openPath.value[0];
    // Runs before the panel unmounts. Focus inside it would fall to the page:
    // hand it to the entry, or to „…“ when the entry moved there. A disabled
    // entry cannot take focus — then the first enabled root entry takes it,
    // so the bar keeps a tab stop.
    const active = document.activeElement;
    const focusInPanel = active instanceof Element && !!active.closest('[data-navbar-panel]');
    exitMenu();
    const target = node.parent ?? node;
    const next = target.disabled ? firstEnabledRoot() : target;
    if (focusInPanel && next) setFocused(next);
  });

  /** The first root entry (or „…“) that can take focus, if any. */
  function firstEnabledRoot(): NavBarNode | null {
    for (const entry of rootRow.value) {
      const node = entryAsNode(entry);
      if (node && !node.disabled) return node;
    }
    return null;
  }

  const rootRow = computed<RootRowEntry[]>(() =>
    buildRootRow(leftItems.value, rightItems.value, maxVisibleLeft.value, OVERFLOW_MARKER)
  );

  function entryAsNode(entry: RootRowEntry): NavBarNode | null {
    if ('isOverflow' in entry) return overflowNode.value;
    return entry;
  }

  function focusEl(id: string) {
    nextTick(() => {
      const el = document.querySelector<HTMLElement>(`[data-id="${CSS.escape(id)}"]`);
      el?.focus();
    });
  }

  function setFocused(node: NavBarNode | null) {
    focusedNode.value = node;
    if (node) focusEl(node.id);
  }

  function openTo(node: NavBarNode) {
    openPath.value = ancestorOpenPath(node);
    menuActive.value = true;
  }

  function closeMenu(node: NavBarNode) {
    const idx = openPath.value.indexOf(node);
    if (idx >= 0) openPath.value = openPath.value.slice(0, idx);
  }

  function closeAll() {
    // Closes all popovers but stays in menu mode so subsequent hovers reopen.
    // Use `exitMenu` to fully leave (Escape, outside-click, leaf action).
    openPath.value = [];
  }

  function exitMenu() {
    openPath.value = [];
    menuActive.value = false;
  }

  /** Close the open panel; `returnFocus` puts focus back on its entry. */
  function closePanel(returnFocus: boolean) {
    if (!panelOpen.value) return;
    const node = openPath.value[0];
    exitMenu();
    if (returnFocus) setFocused(node);
  }

  /** Move focus into the (already open) panel of `node`. */
  function focusIntoPanel(node: NavBarNode) {
    nextTick(() => focusPanel(document.getElementById(panelIdOf(node))));
  }

  function activate(node: NavBarNode, event: PointerEvent | KeyboardEvent) {
    if (node.disabled) return;
    if (node.children.length > 0) {
      // Toggle popover.
      if (openPath.value.includes(node)) closeMenu(node);
      else openTo(node);
      setFocused(node);
    } else if (opensPanel(node)) {
      // Toggle the panel. Opening moves focus into it (NavBarPanel); the entry
      // stays the bar's focused node so arrows and Escape know where they are.
      if (openPath.value.includes(node)) {
        exitMenu();
        setFocused(node);
      } else {
        openPath.value = [node];
        menuActive.value = false;
        focusedNode.value = node;
      }
    } else {
      node.action?.(event);
      exitMenu();
    }
  }

  function hover(node: NavBarNode) {
    // Only follow the mouse while the user is in menu mode (a menu was opened
    // and not yet exited). This prevents stray hovers from grabbing focus.
    if (!menuActive.value) return;
    if (node.parent === null) {
      // Root level — switch root menu. If the new root has no submenu, close
      // any open popover but keep menu mode alive so hovering a sibling root
      // with a submenu reopens that submenu.
      if (node.children.length > 0) openTo(node);
      else closeAll();
      setFocused(node);
    } else {
      // Sub level — open along this branch.
      openTo(node);
      setFocused(node);
    }
  }

  function onRootKey(event: KeyboardEvent, currentEntry: RootRowEntry) {
    const row = rootRow.value;
    const idx = row.findIndex((e) => e.id === currentEntry.id);
    if (idx < 0) return;

    switch (event.key) {
      case 'ArrowLeft': {
        event.preventDefault();
        const next = siblingAt(row, idx, -1, false);
        if (!next && panelOpen.value) exitMenu(); // at the edge: still closes
        if (next) {
          const node = entryAsNode(next);
          if (node) {
            // An open panel closes; it never hands over to the neighbour's menu.
            if (panelOpen.value) exitMenu();
            else if (openPath.value.length > 0 && node.children.length > 0) openTo(node);
            else if (openPath.value.length > 0) closeAll();
            setFocused(node);
          }
        }
        break;
      }
      case 'ArrowRight': {
        event.preventDefault();
        const next = siblingAt(row, idx, +1, false);
        if (!next && panelOpen.value) exitMenu(); // at the edge: still closes
        if (next) {
          const node = entryAsNode(next);
          if (node) {
            // An open panel closes; it never hands over to the neighbour's menu.
            if (panelOpen.value) exitMenu();
            else if (openPath.value.length > 0 && node.children.length > 0) openTo(node);
            else if (openPath.value.length > 0) closeAll();
            setFocused(node);
          }
        }
        break;
      }
      case 'ArrowDown': {
        event.preventDefault();
        const node = entryAsNode(currentEntry);
        if (node && node.children.length > 0) {
          openTo(node);
          setFocused(node.children[0]);
        } else if (node && opensPanel(node)) {
          // Opens the panel (focus moves in), or moves into the open one.
          if (openPath.value.includes(node)) focusIntoPanel(node);
          else activate(node, event);
        }
        break;
      }
      case 'Enter':
      case ' ': {
        const node = entryAsNode(currentEntry);
        if (node) {
          event.preventDefault();
          activate(node, event);
        }
        break;
      }
      case 'Escape': {
        // An open panel is no menu mode; Escape on its entry closes it too.
        if (menuActive.value || panelOpen.value) {
          event.preventDefault();
          exitMenu();
        }
        break;
      }
      default: {
        if (event.key.length === 1 && /[a-zA-ZäöüÄÖÜ0-9]/.test(event.key)) {
          event.preventDefault();
          const texts = row.map((e) => entryAsNode(e)?.text ?? '');
          const target = findTypeaheadTarget(texts, idx, event.key);
          if (target !== null) {
            const node = entryAsNode(row[target]);
            if (node) {
              if (panelOpen.value) exitMenu();
              setFocused(node);
            }
          }
        }
      }
    }
  }

  function onSubKey(event: KeyboardEvent, current: NavBarNode) {
    const parent = current.parent;
    if (!parent) return;
    const siblings = parent.children;
    const idx = siblings.indexOf(current);

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        const next = siblingAt(siblings, idx, +1, false);
        if (next) setFocused(next);
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        const next = siblingAt(siblings, idx, -1, false);
        if (next) {
          setFocused(next);
        } else {
          // At top → close current menu, focus parent.
          closeMenu(parent);
          setFocused(parent);
        }
        break;
      }
      case 'ArrowRight': {
        if (current.children.length > 0) {
          event.preventDefault();
          openTo(current);
          setFocused(current.children[0]);
        }
        break;
      }
      case 'ArrowLeft': {
        // Close current menu (parent), focus parent.
        event.preventDefault();
        closeMenu(parent);
        setFocused(parent);
        break;
      }
      case 'Enter':
      case ' ': {
        event.preventDefault();
        activate(current, event);
        break;
      }
      case 'Escape': {
        event.preventDefault();
        exitMenu();
        // Walk up to the root (shallowest ancestor) and refocus its button.
        let root: NavBarNode = parent;
        while (root.parent !== null) root = root.parent;
        setFocused(root);
        break;
      }
      default: {
        if (event.key.length === 1 && /[a-zA-ZäöüÄÖÜ0-9]/.test(event.key)) {
          event.preventDefault();
          const texts = siblings.map((s) => s.text);
          const target = findTypeaheadTarget(texts, idx, event.key);
          if (target !== null) setFocused(siblings[target]);
        }
      }
    }
  }

  return {
    focusedNode,
    openPath,
    rootRow,
    setFocused,
    activate,
    hover,
    closeAll,
    exitMenu,
    closePanel,
    onRootKey,
    onSubKey,
  };
}
